import Cartesian4 from "../Core/Cartesian4.js";
import defer from "../Core/defer.js";
import defined from "../Core/defined.js";
import destroyObject from "../Core/destroyObject.js";
import Matrix4 from "../Core/Matrix4.js";
import PixelFormat from "../Core/PixelFormat.js";
import Plane from "../Core/Plane.js";
import ComputeCommand from "../Renderer/ComputeCommand.js";
import Framebuffer from "../Renderer/Framebuffer.js";
import FramebufferManager from "../Renderer/FramebufferManager.js";
import PixelDatatype from "../Renderer/PixelDatatype.js";
import RenderState from "../Renderer/RenderState.js";
import Texture from "../Renderer/Texture.js";

/**
 * @private
 */
function PickDepth() {
  this._framebuffer = new FramebufferManager();

  this._textureToCopy = undefined;
  this._copyDepthCommand = undefined;

  // Clarity
  this._outputTexture = undefined;
  this._outputFrameBuffer = undefined;
  this._readyPromise = undefined;
  this._scene = undefined;
  this._calculateVolume = undefined;
  this._modelMatrix = undefined;
  this._plane = undefined;
  this.computeCommand = undefined;
  this._lastPlane = undefined;
  this._calculatingVolume = false;
}

Object.defineProperties(PickDepth.prototype, {
  framebuffer: {
    get: function () {
      return this._framebuffer.framebuffer;
    },
  },
});

function updateFramebuffers(pickDepth, context, depthTexture) {
  const width = depthTexture.width;
  const height = depthTexture.height;
  pickDepth._framebuffer.update(context, width, height);
}

function updateCopyCommands(pickDepth, context, depthTexture) {
  if (!defined(pickDepth._copyDepthCommand)) {
    const fs =
      "uniform highp sampler2D u_texture;\n" +
      "varying vec2 v_textureCoordinates;\n" +
      "void main()\n" +
      "{\n" +
      "    gl_FragColor = czm_packDepth(texture2D(u_texture, v_textureCoordinates).r);\n" +
      "}\n";
    pickDepth._copyDepthCommand = context.createViewportQuadCommand(fs, {
      renderState: RenderState.fromCache(),
      uniformMap: {
        u_texture: function () {
          return pickDepth._textureToCopy;
        },
      },
      owner: pickDepth,
    });
  }

  pickDepth._textureToCopy = depthTexture;
  pickDepth._copyDepthCommand.framebuffer = pickDepth.framebuffer;
}

PickDepth.prototype.update = function (context, depthTexture) {
  updateFramebuffers(this, context, depthTexture);
  updateCopyCommands(this, context, depthTexture);
  getDepthVolume(this);
};

const scratchPackedDepth = new Cartesian4();
const packedDepthScale = new Cartesian4(
  1.0,
  1.0 / 255.0,
  1.0 / 65025.0,
  1.0 / 16581375.0
);

PickDepth.prototype.getDepth = function (context, x, y) {
  // If this function is called before the framebuffer is created, the depth is undefined.
  if (!defined(this.framebuffer)) {
    return undefined;
  }

  const pixels = context.readPixels({
    x: x,
    y: y,
    width: 1,
    height: 1,
    framebuffer: this.framebuffer,
  });

  const packedDepth = Cartesian4.unpack(pixels, 0, scratchPackedDepth);
  Cartesian4.divideByScalar(packedDepth, 255.0, packedDepth);
  return Cartesian4.dot(packedDepth, packedDepthScale);
};

PickDepth.prototype.executeCopyDepth = function (context, passState) {
  this._copyDepthCommand.execute(context, passState);
};

PickDepth.prototype.isDestroyed = function () {
  return false;
};

PickDepth.prototype.destroy = function () {
  this._framebuffer.destroy();
  if (defined(this._copyDepthCommand)) {
    this._copyDepthCommand.shaderProgram =
      defined(this._copyDepthCommand.shaderProgram) &&
      this._copyDepthCommand.shaderProgram.destroy();
  }

  return destroyObject(this);
};

// Clarity
function getDepthVolume(pickDepth) {
  if (!pickDepth._calculateVolume || !pickDepth._scene) {
    return;
  }

  var context = pickDepth._scene.context;
  var computeEngine = pickDepth._scene._computeEngine;
  if (!pickDepth._depthTexture) {
    return 0;
  }

  var volume = 0;
  if (!pickDepth._outputTexture) {
    pickDepth._outputTexture = new Texture({
      context: context,
      width: pickDepth._depthTexture.width,
      height: pickDepth._depthTexture.height,
      pixelFormat: PixelFormat.RGBA,
      pixelDatatype: PixelDatatype.UNSIGNED_BYTE,
    });

    pickDepth._outputFrameBuffer = new Framebuffer({
      context: context,
      colorTextures: [pickDepth._outputTexture],
      destroyAttachments: false,
    });

    var fs =
      pickDepth._useLogDepth ? "#define LOG_DEPTH\n" : "" +
      "uniform sampler2D u_texture;\n" +
      "uniform vec4 u_plane;\n" +
      "varying vec2 v_textureCoordinates;\n" +
      "void main()\n" +
      "{\n" +
      "    float z_window = czm_unpackDepth(texture2D(u_texture, v_textureCoordinates));\n" +
      "    z_window = czm_reverseLogDepth(z_window); \n" +
      "    vec4 positionEC = czm_windowToEyeCoordinates(gl_FragCoord.xy, z_window);\n" +
      "    positionEC = positionEC / positionEC.w;\n" +
      "    float amount = max(0.0, czm_planeDistance(u_plane, positionEC.xyz)) / u_plane.w;\n" +
      "    gl_FragColor = czm_packDepth(amount);\n" +
      "}\n";

    pickDepth.computeCommand = new ComputeCommand({
      fragmentShaderSource: fs,
      outputTexture: pickDepth._outputTexture,
      uniformMap: {
        u_texture: function () {
          return pickDepth._depthTexture;
        },
        u_plane: function () {
          var scratchClippingPlaneMatrix = new Matrix4();
          Matrix4.multiply(
            context.uniformState.view3D,
            pickDepth._modelMatrix,
            scratchClippingPlaneMatrix
          );
          var plane = Plane.transform(
            pickDepth._plane,
            scratchClippingPlaneMatrix
          );
          return new Cartesian4(
            plane.normal.x,
            plane.normal.y,
            plane.normal.z,
            plane.distance
          );
        },
      },
      persists: false,
    });

    pickDepth._lastPlane = pickDepth.computeCommand.uniformMap.u_plane();
    pickDepth.computeCommand.execute(computeEngine);
  } else if (!pickDepth._calculatingVolume) {
    var readState = {
      framebuffer: pickDepth._outputFrameBuffer,
      x: 0,
      y: 0,
      width: pickDepth._depthTexture.width,
      height: pickDepth._depthTexture.height,
    };

    var pixels = context.readPixels(readState);
    var sum = 0.0;
    var max = 0.0;
    pickDepth._calculatingVolume = true;

    try {
      var pixelSize = pickDepth._scene.camera.frustum.getPixelDimensions(
        pickDepth._scene.drawingBufferWidth,
        pickDepth._scene.drawingBufferHeight,
        1.0,
        pickDepth._scene.pixelRatio,
        new Cesium.Cartesian2()
      );
      var plane = Plane.transform(pickDepth._plane, pickDepth._modelMatrix);
      var distanceToPlane = Plane.getPointDistance(
        plane,
        pickDepth._scene.camera.position
      );
      for (var i = 0; i < pixels.length; i += 4) {
        if (pixels[0] != pixels[i]) {
          // Unpack Depth and multiply with perspective
          var packedCart = new Cartesian4(
            pixels[i],
            pixels[i + 1],
            pixels[i + 2],
            pixels[i + 3]
          );
          Cartesian4.divideByScalar(packedCart, 255.0, packedCart);
          var height =
            Cartesian4.dot(packedCart, packedDepthScale) *
            pickDepth._lastPlane.w;
          // Calculate volume using pixel size scaled with distance
          sum +=
            height *
            pixelSize.x *
            pixelSize.y *
            Math.pow(distanceToPlane - height, 2);
          if (height > max) {
            max = height;
          }
        }
      }
      // console.log(max, distanceToPlane, pixelSize);
    } catch (e) {}

    volume = sum;
    pickDepth._calculateVolume = false;
    pickDepth._scene = undefined;
    pickDepth._outputTexture.destroy();
    pickDepth._outputFrameBuffer.destroy();
    pickDepth._readyPromise.resolve(volume);
    pickDepth._outputTexture = undefined;
    pickDepth._outputFrameBuffer = undefined;
  }
}

// Clarity
PickDepth.prototype.getDepthVolume = function (scene, modelMatrix, plane) {
  this._scene = scene;
  this._calculatingVolume = false;
  this._calculateVolume = true;
  this._modelMatrix = modelMatrix;
  this._plane = Plane.clone(plane);
  this._readyPromise = defer();
  return this._readyPromise.promise;
};

export default PickDepth;
